import Vue from "vue";
import VueRouter, { Route, RouteConfig } from "vue-router";

import Container from "../../encoding/components/Container.vue";

import store from "../store/index";

import { Trans } from "@/shared/scripts/helpers/translation/translation";
import { RouterHelper } from "@/shared/scripts/helpers/router/router-helper";
import { getCookie } from "@/shared/scripts/helpers/cookie/cookie";
import { datadogStartView } from "@/shared/scripts/datadog";

import Welcome from "@/optimizedEncoding/Welcome.vue";

import Classifieds from "@/views/Classifieds.vue";

// Encoding
const Encoding = () => import(/* webpackChunkName: "encoding" */ "../../views/Encoding.vue");
const Price = () => import(/* webpackChunkName: "encoding" */ "../../encoding/pages/Price.vue");
const Pictures = () => import(/* webpackChunkName: "encoding" */ "../../encoding/pages/Pictures.vue");
const Specification = () => import(/* webpackChunkName: "encoding" */ "../../encoding/pages/Specification.vue");
const TitleDescription = () => import(/* webpackChunkName: "encoding" */ "../../encoding/pages/TitleDescription.vue");
const ContactInformation = () => import(/* webpackChunkName: "encoding" */ "../../encoding/pages/ContactInformation.vue");
const Interior = () => import(/* webpackChunkName: "encoding" */ "../../encoding/pages/Interior.vue");
const Exterior = () => import(/* webpackChunkName: "encoding" */ "../../encoding/pages/Exterior.vue");
const Energy = () => import(/* webpackChunkName: "encoding" */ "../../encoding/pages/Energy.vue");
const PropertyAddress = () => import(/* webpackChunkName: "encoding" */ "../../encoding/pages/PropertyAddress.vue");
const Confirmation = () => import(/* webpackChunkName: "encoding" */ "../../views/Confirmation.vue");
const BasicInformation = () => import(/* webpackChunkName: "encoding" */ "../../encoding/pages/BasicInformation.vue");
const Regulations = () => import(/* webpackChunkName: "encoding" */ "../../encoding/pages/Regulations.vue");
const Pricing = () => import(/* webpackChunkName: "encoding" */ "../../encoding/pages/Pricing.vue");
const Payment = () => import(/* webpackChunkName: "encoding" */ "../../encoding/pages/Payment.vue");
const EditPictures = () => import(/* webpackChunkName: "encoding" */ "../../encoding/pages/EditPictures.vue");
const PaymentStatus = () => import(/* webpackChunkName: "encoding" */ "../../views/PaymentStatus.vue");

//Optimized Encoding
const OptimizedEncoding = () => import(/* webpackChunkName: "optimized-encoding" */ "../../optimizedEncoding/OptimizedEncoding.vue");
// Dashboard
const MyClassifieds = () => import(/* webpackChunkName: "my-immoweb" */ "../../myImmoweb/pages/MyClassifieds.vue");
// Extend
const Extend = () => import(/* webpackChunkName: "visibility" */ "../../views/Extend.vue");

// Visibility
const VisibilityUpgrade = () => import(/* webpackChunkName: "visibility" */ "../../views/VisibilityUpgrade.vue");
const VisibilityUpgradeSelection = () => import(/* webpackChunkName: "visibility" */ "../../myImmoweb/visibility/Selection.vue");

// Errors
const Error404 = () => import(/* webpackChunkName: "errors" */ "../../views/Error404.vue");

// get current local from cookie (Not use for at the moment)
let locale = getCookie("LANGUAGE") as string;

if (locale === undefined || !locale.length) {
    locale = "en";
    Trans.changeLocale(locale);
}

Vue.use(VueRouter);

/**
 * Route are defined to match first the language @locale input
 * Alias are use to defined route in other language but match the same url
 * Name of route must match the translation file for translation purpose
 */
const routes: Array<RouteConfig> = [
    {
        path: "/",
        beforeEnter: Trans.routeMiddleware,
    },
    {
        path: "/:locale",
        component: Classifieds,
        beforeEnter: Trans.routeMiddleware,
        name: "route-base",
    },
    {
        path: "/:locale/increase-visibility",
        component: VisibilityUpgrade,
        alias: ["/:locale/augmenter-visibilite", "/:locale/zichtbaarheid-vergroten"],
        redirect: "/:locale/increase-visibility/select-package/:id?",
        beforeEnter: RouterHelper.guardRetrieveClassified,
        name: "upgrade-visibility",
        meta: {
            authRequired: true
        },
        props: route => ({ id: route.params.id, action: "UPGRADE" }),
        children: [
            {
                path: "select-package/:id?",
                name: "visibility-upgrade-choice",
                component: VisibilityUpgradeSelection,
                alias: ["selection-package/:id?", "selecteer-pakket/:id?"],
                props: route => ({ id: route.params.id, action: "UPGRADE" }),
                beforeEnter: Trans.routeMiddleware,
            },
            {
                path: "payment/:id?",
                name: "visibility-upgrade-payment",
                component: Payment,
                alias: ["paiement/:id?", "betaling/:id?"],
                props: route => ({ id: route.params.id, action: "UPGRADE" }),
                beforeEnter: Trans.routeMiddleware,
            },
        ]
    },
    {
        path: "/:locale/extend-classified",
        component: Extend,
        beforeEnter: RouterHelper.guardRetrieveClassified,
        alias: ["/:locale/prolonger-annonce", "/:locale/verlengen-zoekertje"],
        name: "extend-classified",
        meta: {
            authRequired: true
        },
        props: ({action: "EXTENSION"}),
        children: [
            {
                path: "select-package/:id",
                name: "extend-choice",
                component: Pricing,
                alias: ["selection-package/:id?", "tarieven/:id?"],
                props: route => ({ id: route.params.id, action: "EXTENSION" }),
                beforeEnter: Trans.routeMiddleware,
            },
            {
                path: "payment/:id",
                name: "extend-payment",
                component: Payment,
                alias: ["paiement/:id?", "betaling/:id?"],
                props: {action: "EXTENSION"},
                beforeEnter: Trans.routeMiddleware,
            },
        ]
    },
    {
        path: "/:locale/encoding",
        component: Encoding,
        alias: ["/:locale/encodage", "/:locale/coderen"],
        redirect: "/:locale/encoding/basic-information/property-type",
        beforeEnter: RouterHelper.guardToStepRoute,
        children: [
            {
                path: "basic-information",
                name: "Container",
                component: Container,
                alias: ["information-de-base", "basis-informatie"],
                beforeEnter: Trans.routeMiddleware,
                children: [
                    {
                        path: "property-type/:id?",
                        name: "step-property-type",
                        alias: ["type-de-propriete/:id?", "zoekactie-type/:id?"],
                        component: BasicInformation,
                    },
                    {
                        path: "property-address/:id?",
                        name: "step-property-address",
                        alias: ["adresse-de-propriete/:id?", "zoekactie-adres/:id?"],
                        component: PropertyAddress,
                    },
                    {
                        path: "pictures/:id?",
                        name: "step-pictures",
                        alias: ["images/:id?", "fotos/:id?"],
                        component: Pictures,
                    },
                    {
                        path: "property-price/:id?",
                        name: "step-property-price",
                        alias: ["prix-de-propriete/:id?", "prijs-zoekactie-type/:id?"],
                        component: Price,
                    },
                ]
            },
            {
                path: "specification",
                name: "step-specification",
                component: Container,
                alias: ["specificatie"],
                beforeEnter: Trans.routeMiddleware,
                children: [
                    {
                        path: "general/:id?",
                        name: "step-general",
                        alias: ["algemeen/:id?"],
                        component: Specification,
                    },
                    {
                        path: "interior/:id?",
                        name: "step-interior",
                        alias: ["interieur/:id?", "binnenkant/:id?"],
                        component: Interior,
                    },
                    {
                        path: "exterior/:id?",
                        name: "step-exterior",
                        alias: ["exterieur/:id?", "buitenkant/:id?"],
                        component: Exterior,
                    },
                    {
                        path: "energy/:id?",
                        name: "step-energy",
                        alias: ["energie/:id?","energie-prestatie/:id?"],
                        component: Energy,
                    },
                    {
                        path: "regulations/:id?",
                        name: "step-regulations",
                        alias: ["regelgeving-en-vergunningen/:id?"],
                        component: Regulations,
                    }
                ]
            },
            {
                path: "title-description/:id?",
                name: "step-title-description",
                component: TitleDescription,
                beforeEnter: Trans.routeMiddleware,
                alias: ["titre-description/:id?", "titel-en-beschrijving/:id?"]
            },
            {
                path: "contact-information/:id?",
                name: "step-contact-information",
                component: ContactInformation,
                beforeEnter: Trans.routeMiddleware,
                alias: ["information-de-contact/:id?", "jouw-gegevens/:id?"],
                meta: {
                    authRequired: true
                }
            },
            {
                path: "pricing/:id?",
                name: "step-pricing",
                component: Pricing,
                beforeEnter: Trans.routeMiddleware,
                alias: ["prijs/:id?", "prix/:id?"],
                meta: {
                    authRequired: true
                }
            },
            {
                path: "payment/:id?",
                name: "step-payment",
                component: Payment,
                beforeEnter: Trans.routeMiddleware,
                alias: ["betaling/:id?", "paiement/:id?"],
            },
        ]
    },
    {
        path: "/:locale/encoding/welcome",
        name: "route-welcome",
        component: Welcome,
        beforeEnter: Trans.routeMiddleware,
        alias: ["/:locale/coderen/welkom", "/:locale/encodage/bienvenue"]
    },
    {
        path: "/:locale/optimized-encoding/:id?",
        name: "route-optimized-encoding",
        component: OptimizedEncoding,
        beforeEnter: Trans.routeMiddleware,
        alias: ["/:locale/encodage-optimise/:id?", "/:locale/geoptimaliseerde-coderen/:id?"]
    },
    {
        path: "/:locale/my-classifieds",
        component: MyClassifieds,
        beforeEnter: Trans.routeMiddleware,
        name: "my-immoweb",
        alias: ["/:locale/mijn-zoekertjes", "/:locale/mes-annonces"],
        meta: {
            authRequired: true
        }
    },
    {
        path: "/:locale/add-modify-pictures/:id?",
        component: EditPictures,
        beforeEnter: Trans.routeMiddleware,
        name: "add-modify-pictures",
        alias: ["/:locale/ajouter-modifier-photos/:id?", "/:locale/fotos-toevoegen-of-wijzigen/:id?"]
    },
    {
        path: "/:locale/:id/payment-status",
        name: "payment-status",
        component: PaymentStatus,
        beforeEnter: Trans.routeMiddleware
    },
    {
        path: "/:locale/confirmation/:id?",
        component: Confirmation,
        name: "confirmation",
        beforeEnter: Trans.routeMiddleware,
        alias: ["/:locale/bevestiging/:id?"]
    },
    {
        path: "/about",
        name: "About",
        // route level code-splitting
        // this generates a separate chunk (about.[hash].js) for this route
        // which is lazy-loaded when the route is visited.
        component: () => import(/* webpackChunkName: "about" */ "../../views/About.vue")
    },
    {
        path: "/*",
        name: "404",
        component: Error404,
    },
];

export const router = new VueRouter({
    mode: "hash",
    base: process.env.BASE_URL,
    routes: routes,
    scrollBehavior: function(to, from, savedPosition) {
        if (savedPosition) {
            return savedPosition;
        } else {
            return { x: 0, y: 0 };
        }
    }
});

router.beforeEach((to, from, next) => {
    // RouterHelper.redirectBadRoot(to, from, next);

    /** Make sure correct classified is used when the classified ID is manually
     * changed in the address bar. */
    if (to.params && from.params) {
        if (to.params.id && from.params.id) {
            if (to.params.id !== from.params.id) {
                store.dispatch("FormModule/resetCriteria");
                RouterHelper.guardToStepRoute(to, from, next);
            }
        }
    }
    next();
});

/**
 * Set the current step after each route changes
 * Taking into account "back & forward" from the browser
 */
router.afterEach((to) => {

    validationStep(to);

    datadogStartView(to.name ?? to.path);

    const appNode = document.getElementById("app");

    if (appNode) {
        appNode.focus();
    }

});


const validationStep = (to: Route) => {

    // const validateStep = store.getters["StepsWizard/getValidatedSteps"] as Array<string>;
    // if (to.name && to.name.match("step") && to.name !== "step-confirmation") {
    //     if (validateStep.indexOf(to.name) === -1) {
    //         router.push(from.path);
    //         return;
    //     }
    // }

    const step = store.getters["StepsWizard/getStep"](to.name);

    if(step) {
        store.dispatch("StepsWizard/setPreviousStepToDone", step.step);
        store.dispatch("StepsWizard/current", step);
    }
};

export default router;
